import { useEffect, useState } from 'react';
import { ApiError, api } from '../lib/api';
import {
  DEFAULT_FONT,
  DEFAULT_MONO,
  DEFAULT_PALETTE_ID,
  DEFAULT_SERIF,
  MONO_FONT_OPTIONS,
  PALETTE_PRESETS,
  SANS_FONT_OPTIONS,
  SERIF_FONT_OPTIONS,
} from '../lib/palettes';
import { applyTheme, readSettingsTheme } from '../lib/theme';
import { useEscapeKey } from '../lib/useEscapeKey';
import { useToast } from './Toast';
import { Icon } from './icons';

const DEFAULT_CUSTOM = PALETTE_PRESETS[0]?.colors ?? [];

type Section = 'palette' | 'connections' | 'typography';

const SECTIONS: { id: Section; label: string; icon: JSX.Element }[] = [
  {
    id: 'palette',
    label: 'Paleta',
    icon: (
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path d="M12 22a10 10 0 1 1 0-20 7 7 0 0 1 7 7c0 3-2 4-4 4h-2a2 2 0 0 0 0 4 3 3 0 0 1-1 5z" />
        <circle cx="7" cy="13" r="1" fill="currentColor" />
        <circle cx="9" cy="8" r="1" fill="currentColor" />
        <circle cx="14" cy="6" r="1" fill="currentColor" />
        <circle cx="17" cy="11" r="1" fill="currentColor" />
      </svg>
    ),
  },
  {
    id: 'connections',
    label: 'Conexiones',
    icon: (
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path d="M4 9h6v6H4zM14 9h6v6h-6zM10 12h4" />
      </svg>
    ),
  },
  {
    id: 'typography',
    label: 'Tipografía',
    icon: (
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path d="M4 7V5h16v2M9 5v14M9 19h6" />
      </svg>
    ),
  },
];

export function SetupModal({
  initialSettings,
  onClose,
  onSaved,
}: {
  initialSettings: Record<string, unknown>;
  onClose: () => void;
  onSaved: (settings: Record<string, unknown>) => void;
}) {
  useEscapeKey(cancel);
  const initial = readSettingsTheme(initialSettings);
  const [section, setSection] = useState<Section>('palette');
  const [activePreset, setActivePreset] = useState<string>(initial.palette ?? DEFAULT_PALETTE_ID);
  const [custom, setCustom] = useState<string[]>(initial.customPalette ?? DEFAULT_CUSTOM);
  const [fontSans, setFontSans] = useState<string>(initial.typography ?? DEFAULT_FONT);
  const [fontSerif, setFontSerif] = useState<string>(initial.typographySerif ?? DEFAULT_SERIF);
  const [fontMono, setFontMono] = useState<string>(initial.typographyMono ?? DEFAULT_MONO);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { push } = useToast();

  // Apply live preview whenever selection changes.
  useEffect(() => {
    applyTheme({
      palette: activePreset,
      customPalette: custom,
      typography: fontSans,
      typographySerif: fontSerif,
      typographyMono: fontMono,
    });
  }, [activePreset, custom, fontSans, fontSerif, fontMono]);

  async function applyAndClose() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.patchSettings({
        palette: activePreset,
        customPalette: custom,
        typography: fontSans,
        typographySerif: fontSerif,
        typographyMono: fontMono,
      });
      onSaved(r.settings);
      push('Configuración aplicada');
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  function cancel() {
    // Revert live preview to whatever was originally loaded.
    applyTheme(initial);
    onClose();
  }

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: pointer-only affordance. The dialog is dismissable from the keyboard via useEscapeKey and the header close button, so the backdrop needs no key handler of its own.
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) cancel();
      }}
    >
      <div className="modal setup-modal">
        <aside className="setup-side">
          <div className="setup-side-title">Setup</div>
          <div className="setup-side-h2">Dashboard</div>
          {SECTIONS.map((s) => (
            <button
              type="button"
              key={s.id}
              className={`setup-nav ${section === s.id ? 'active' : ''}`}
              onClick={() => setSection(s.id)}
            >
              {s.icon} {s.label}
            </button>
          ))}
          <div className="setup-side-foot">
            Cambios aplicados en vivo. Cierra para volver al dashboard.
          </div>
        </aside>
        <main className="setup-content">
          {section === 'palette' && (
            <PaletteSection
              activePreset={activePreset}
              setActivePreset={setActivePreset}
              custom={custom}
              setCustom={setCustom}
            />
          )}
          {section === 'connections' && <ConnectionsSection />}
          {section === 'typography' && (
            <TypographySection
              fontSans={fontSans}
              setFontSans={setFontSans}
              fontSerif={fontSerif}
              setFontSerif={setFontSerif}
              fontMono={fontMono}
              setFontMono={setFontMono}
            />
          )}

          {error && (
            <div className="error-banner" style={{ marginTop: 12 }}>
              {error}
            </div>
          )}

          <div className="setup-foot">
            <div className="setup-foot-note">
              Cambios aplicados en vivo. Pulsa "Aplicar y cerrar" para guardar.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn btn-ghost" onClick={cancel} disabled={busy}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={applyAndClose}
                disabled={busy}
              >
                {busy ? 'Guardando…' : 'Aplicar y cerrar'}
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

function PaletteSection({
  activePreset,
  setActivePreset,
  custom,
  setCustom,
}: {
  activePreset: string;
  setActivePreset: (id: string) => void;
  custom: string[];
  setCustom: (next: string[]) => void;
}) {
  return (
    <>
      <h2 style={{ fontSize: 22, fontWeight: 400, margin: '0 0 8px', letterSpacing: '-0.01em' }}>
        Paleta de <span className="serif-i">colores</span>
      </h2>
      <p
        style={{
          color: 'var(--fg-3)',
          fontSize: 13,
          margin: '0 0 22px',
          maxWidth: 560,
          lineHeight: 1.55,
        }}
      >
        10 colores que se usan como sugerencias por defecto al crear un nuevo{' '}
        <strong>Message type</strong>, y para series neutras en gráficas (sparklines, barras
        apiladas). No toca los message types ya creados — para eso ve a <em>Message types</em>.
      </p>
      <div className="label-xs" style={{ marginBottom: 8 }}>
        Presets
      </div>
      <div className="preset-grid">
        {PALETTE_PRESETS.map((p) => (
          <button
            type="button"
            key={p.id}
            className={`preset ${activePreset === p.id ? 'active' : ''}`}
            onClick={() => setActivePreset(p.id)}
          >
            <div className="preset-swatches">
              {p.colors.map((c, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: the palette is a fixed-length list of slots, so the position is the identity; two slots may legitimately hold the same colour.
                <span key={i} className="preset-sw" style={{ background: c }} />
              ))}
            </div>
            <div className="preset-name">{p.name}</div>
            <div className="preset-desc">{p.desc}</div>
          </button>
        ))}
        <button
          type="button"
          className={`preset ${activePreset === 'custom' ? 'active' : ''}`}
          onClick={() => setActivePreset('custom')}
          style={{ borderStyle: activePreset === 'custom' ? 'solid' : 'dashed' }}
        >
          <div className="preset-swatches">
            {custom.map((c, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: the palette is a fixed-length list of slots, so the position is the identity; two slots may legitimately hold the same colour.
              <span key={i} className="preset-sw" style={{ background: c }} />
            ))}
          </div>
          <div className="preset-name">
            Mi paleta <span style={{ fontSize: 10, color: 'var(--accent)' }}>· custom</span>
          </div>
          <div className="preset-desc">10 colores editables abajo</div>
        </button>
      </div>

      <div className="label-xs" style={{ marginTop: 26, marginBottom: 10 }}>
        Editar mi paleta
      </div>
      <div
        style={{
          background: 'var(--bg-2)',
          border: '1px solid var(--border-1)',
          borderRadius: 10,
          padding: 16,
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
          {custom.map((c, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: the palette is a fixed-length list of slots, so the position is the identity; two slots may legitimately hold the same colour.
            <div key={i} style={{ position: 'relative' }}>
              <label style={{ display: 'block', cursor: 'pointer' }}>
                <div
                  style={{
                    height: 48,
                    borderRadius: 8,
                    background: c,
                    border: '1px solid var(--border-2)',
                    marginBottom: 6,
                  }}
                />
                <input
                  type="color"
                  value={c}
                  onChange={(e) => setCustom(custom.map((x, j) => (j === i ? e.target.value : x)))}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    opacity: 0,
                    width: '100%',
                    height: 48,
                    cursor: 'pointer',
                  }}
                />
              </label>
              <input
                className="input mono"
                style={{ fontSize: 10.5, padding: '4px 6px', textAlign: 'center' }}
                value={c}
                onChange={(e) => setCustom(custom.map((x, j) => (j === i ? e.target.value : x)))}
              />
            </div>
          ))}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginTop: 14,
            paddingTop: 14,
            borderTop: '1px solid var(--border-1)',
          }}
        >
          <button
            type="button"
            className="btn btn-accent"
            onClick={() => setActivePreset('custom')}
            disabled={activePreset === 'custom'}
          >
            {activePreset === 'custom' ? (
              <>
                <Icon.Check size={11} /> Mi paleta activa
              </>
            ) : (
              'Activar mi paleta'
            )}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setCustom(DEFAULT_CUSTOM)}>
            <Icon.Refresh size={11} /> Restaurar
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              const src = PALETTE_PRESETS.find(
                (p) => p.id === (activePreset === 'custom' ? 'fresh' : activePreset),
              );
              if (src) setCustom([...src.colors]);
            }}
          >
            Copiar desde preset activa
          </button>
        </div>
        <div className="field-hint" style={{ marginTop: 10 }}>
          Pulsa un swatch para abrir el color picker, o edita el hex directamente. La paleta se
          guarda al pulsar "Aplicar y cerrar".
        </div>
      </div>
    </>
  );
}

function ConnectionsSection() {
  const items = [
    { name: 'Webhooks', desc: 'Envío HTTP POST a un endpoint propio.' },
    { name: 'Email alerts (avanzado)', desc: 'Plantillas personalizadas, listas de distribución.' },
    { name: 'Discord', desc: 'Notificación a un canal o usuario.' },
    { name: 'Slack', desc: 'Mensajes a un workspace y canal.' },
  ];
  return (
    <>
      <h2 style={{ fontSize: 22, fontWeight: 400, margin: '0 0 8px' }}>
        Conexiones <span className="serif-i">futuras</span>
      </h2>
      <p style={{ color: 'var(--fg-3)', fontSize: 13, margin: '0 0 22px', maxWidth: 520 }}>
        Integraciones que aparecerán como canales en las reglas de alerta. El email funcional se
        configura desde la propia vista <strong>Alerts</strong>.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map((c) => (
          <div
            key={c.name}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: 14,
              background: 'var(--bg-2)',
              border: '1px solid var(--border-1)',
              borderRadius: 10,
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500, fontSize: 13.5 }}>{c.name}</div>
              <div style={{ fontSize: 11.5, color: 'var(--fg-3)' }}>{c.desc}</div>
            </div>
            <span
              className="chip"
              style={{
                background: 'transparent',
                color: 'var(--fg-4)',
                borderColor: 'var(--border-2)',
              }}
            >
              Coming soon
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

function TypographySection({
  fontSans,
  setFontSans,
  fontSerif,
  setFontSerif,
  fontMono,
  setFontMono,
}: {
  fontSans: string;
  setFontSans: (f: string) => void;
  fontSerif: string;
  setFontSerif: (f: string) => void;
  fontMono: string;
  setFontMono: (f: string) => void;
}) {
  return (
    <>
      <h2 style={{ fontSize: 22, fontWeight: 400, margin: '0 0 8px' }}>Tipografía</h2>
      <p style={{ color: 'var(--fg-3)', fontSize: 13, margin: '0 0 22px', maxWidth: 560 }}>
        Tres familias independientes: <strong>Sans</strong> es la base de la UI,{' '}
        <strong>Serif</strong> son los acentos itálicos (p. ej. "observando" en el saludo),{' '}
        <strong>Mono</strong> los números de KPIs y los chips de code.
      </p>

      <FontPickerBlock
        title="Sans"
        subtitle="Texto general, navegación, botones."
        options={SANS_FONT_OPTIONS as unknown as string[]}
        value={fontSans}
        onChange={setFontSans}
        category="sans"
      />

      <FontPickerBlock
        title={
          <>
            Serif{' '}
            <span className="serif-i" style={{ fontSize: 13 }}>
              acentos
            </span>
          </>
        }
        subtitle="Itálicas decorativas, títulos de página, palabras destacadas."
        options={SERIF_FONT_OPTIONS as unknown as string[]}
        value={fontSerif}
        onChange={setFontSerif}
        category="serif"
      />

      <FontPickerBlock
        title="Mono"
        subtitle="Números (KPIs, contadores), códigos hex, chips de message type."
        options={MONO_FONT_OPTIONS as unknown as string[]}
        value={fontMono}
        onChange={setFontMono}
        category="mono"
      />

      <div className="field-hint" style={{ marginTop: 16 }}>
        Los cambios se aplican al pulsar y se guardan al cerrar.
      </div>
    </>
  );
}

function FontPickerBlock({
  title,
  subtitle,
  options,
  value,
  onChange,
  category,
}: {
  title: React.ReactNode;
  subtitle: string;
  options: string[];
  value: string;
  onChange: (f: string) => void;
  category: 'sans' | 'serif' | 'mono';
}) {
  const stackOf = (f: string) => {
    if (category === 'sans') return `"${f}", system-ui, sans-serif`;
    if (category === 'serif') return `"${f}", Georgia, serif`;
    return `"${f}", ui-monospace, monospace`;
  };
  const previewItalic = category === 'serif';
  return (
    <div style={{ marginBottom: 22 }}>
      <div className="label-xs" style={{ marginBottom: 4 }}>
        {title}
      </div>
      <div style={{ color: 'var(--fg-4)', fontSize: 11.5, marginBottom: 10 }}>{subtitle}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {options.map((f) => (
          <button
            type="button"
            key={f}
            className={`preset ${value === f ? 'active' : ''}`}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 16,
              padding: 12,
              textAlign: 'left',
            }}
            onClick={() => onChange(f)}
          >
            <div
              style={{
                fontSize: 26,
                fontFamily: stackOf(f),
                fontStyle: previewItalic ? 'italic' : 'normal',
                fontWeight: previewItalic ? 400 : 500,
                minWidth: 64,
              }}
            >
              {category === 'mono' ? '01' : 'Aa'}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13.5, fontWeight: 500, fontFamily: stackOf(f) }}>{f}</div>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--fg-4)',
                  fontFamily: stackOf(f),
                  fontStyle: previewItalic ? 'italic' : 'normal',
                }}
              >
                {category === 'mono'
                  ? '0123456789 — total logs (24h)'
                  : previewItalic
                    ? 'observando — quick brown fox jumps'
                    : 'The quick brown fox jumps · 0123456789'}
              </div>
            </div>
            {value === f && <span style={{ fontSize: 11, color: 'var(--accent)' }}>Activa</span>}
          </button>
        ))}
      </div>
    </div>
  );
}
